// Thẻ của hộp hướng dẫn trang Nhân sự — MỘT nguồn, buộc vào nhãn thật.
//
// Nhãn lấy thẳng từ `STAFF_TAB_LABEL` của màn, và danh sách khoá khai
// `satisfies` theo đúng khoá của từ điển đó: đổi khoá thẻ trên màn là lỗi kiểu
// ngay lúc build, không phải đợi ai mở hộp lên mới thấy bài chỉ tới một thẻ
// không còn tồn tại.

import type { STAFF_TAB_LABEL } from "@/features/staff/labels";
import type { Audience } from "./audience";
import type { StatusTone } from "./GuideSections";
import type { GuideFlow, MultiTabGuideConfig } from "./multi-tab-guides";

/** Id trang trong `nav-items.ts`. Tra bằng hằng số, không gõ chuỗi lần hai. */
export const STAFF_GUIDE_ID = "staff";

/**
 * Thứ tự thẻ trong hộp = thứ tự thẻ trên màn.
 *
 * 🔴 CỐ Ý NGẮN HƠN MÀN — đây là trang đầu tiên trong kho làm vậy, nên đọc kỹ
 * trước khi "sửa cho đủ". Màn có bốn thẻ; thẻ "Đồng bộ dữ liệu" là màn KỸ
 * THUẬT nạp dữ liệu, chỉ vai quản trị hệ thống mở được.
 *
 * Vì sao không viết bài cho nó: `audiencesOf` gộp vai quản trị hệ thống và
 * tầng điều hành vào CHUNG một nhóm `"exec"` (xem `audience.ts`) — hộp không
 * phân biệt được hai người đó. Viết bài cho thẻ ấy là tầng điều hành mở hộp ra
 * đọc trọn bài của một thẻ màn thật của họ KHÔNG có, rồi đi tìm mãi không thấy.
 *
 * Hệ quả đã biết và chấp nhận: người vai quản trị đứng ở thẻ đó bấm Hướng dẫn
 * thì hộp mở về thẻ đầu (`chonThe` trong `MultiTabGuideDialog.tsx`), không báo
 * gì cả. Đúng ý đồ, không phải lỗi.
 *
 * `staff-guide.test.ts` neo lại chủ ý này bằng một bài kiểm riêng.
 */
export const STAFF_GUIDE_TAB_ORDER = [
  "staff",
  "branches",
  "jobs",
] as const satisfies readonly (keyof typeof STAFF_TAB_LABEL)[];

export type StaffGuideTabKey = (typeof STAFF_GUIDE_TAB_ORDER)[number];

/**
 * Nhóm nghề nào MỞ ĐƯỢC thẻ nào trên màn thật.
 *
 *   staff     Ai vào được trang đều thấy — đây là quyển danh bạ của công ty.
 *             Đo từ `lib/auth/page-access.ts`: mọi vai nhân viên phòng ban,
 *             kiểm soát chất lượng, kế toán, trưởng phòng và ba vai chuỗi đều
 *             có trang này. Thẻ bày gì thì máy chủ đã cắt theo phạm vi từng
 *             người, nên bài không cần rào thêm.
 *
 *   branches  Danh mục cơ sở của cả công ty. Cổng là `branch.write`, mà bảng
 *             hành động của máy chủ cấp đúng hai vai cao nhất — KHÔNG có vai
 *             chuỗi. Mở rộng dòng này là phát bài quản trị danh mục cho người
 *             mở màn ra không thấy thẻ đâu.
 *
 *   jobs      Đếm người nhận từng loại việc theo cơ sở (QĐ 0254). Cổng là
 *             `jobchoice.staff.read`: trưởng phòng, trưởng cửa hàng, quản lý
 *             cụm (chỉ xem) và hai vai cao nhất — đúng nhóm "lead" + "exec".
 *             Thợ KHÔNG mở được thẻ này; họ chọn việc ở ô "Việc tôi nhận"
 *             trên Tổng quan.
 */
export const THE_AI_MO_DUOC: Record<StaffGuideTabKey, Audience[]> = {
  staff: [
    "reception",
    "sale",
    "photo",
    "makeup",
    "assistant",
    "retouch",
    "cskh",
    "marketing",
    "accounting",
    "qc",
    "lead",
    "exec",
  ],
  branches: ["exec"],
  jobs: ["lead", "exec"],
};

/** Màu nhãn trạng thái, khớp badge trên màn thật. */
const TONE_THEO_NHAN: Record<string, StatusTone> = {
  "Nghỉ việc": "danger",
  "Ngưng hoạt động": "danger",
  "Thử việc": "warning",
  "Chưa đồng bộ": "warning",
  "Chưa khai": "warning",
  "Cấp đặc cách": "warning",
  "Đặt online": "success",
  "Ca vận hành": "success",
  "Chốt két": "success",
  "Nhóm miền": "info",
  "Chi nhánh": "info",
  "Ô đỏ": "danger",
  "Mặc định · ĐÃ BỎ": "danger",
  "Nhận thêm · xếp sau người chuyên": "warning",
};

/**
 * Vòng đời một hồ sơ nhân sự.
 *
 * Đây là chỗ trả lời câu hỏi mà cả tính năng này sinh ra để trả lời: "sửa cái
 * này thì ảnh hưởng gì". Đứng trong một thẻ thì chỉ thấy nút bấm; dải này cho
 * thấy hồ sơ là GỐC — ghi gì ở đây thì lịch ca, bảng lương và những gì người
 * đó nhìn thấy đều chạy theo.
 *
 * Chặng "Đổi bộ phận hoặc cơ sở" tô vàng chứ không xanh, CỐ Ý: nó là thao tác
 * bình thường nhưng hậu quả lan rộng nhất, và là chỗ người ta hay sửa nhanh
 * rồi ngạc nhiên vì bảng lương tháng đó khác đi.
 */
const STAFF_FLOW: GuideFlow = {
  heading: "Một hồ sơ nhân sự đi qua đâu",
  intro:
    "Các thẻ của trang là những chặng của cùng một hồ sơ. Chặng tô đậm là chỗ thẻ bạn đang mở nhìn vào.",
  stages: [
    {
      ten: "Lập hồ sơ",
      tone: "info",
      ke: "Khai tên, bộ phận, cơ sở chính và mã nhân viên. Hồ sơ là gốc của mọi thứ khác về người này.",
      the: ["staff"],
    },
    {
      ten: "Gắn tài khoản và phạm vi",
      tone: "info",
      ke: "Có tài khoản đăng nhập và có phạm vi cơ sở thì người đó mới đăng nhập được và mới thấy việc của mình.",
      the: ["staff", "branches"],
    },
    {
      ten: "Đang làm việc",
      tone: "success",
      ke: "Bộ phận và cơ sở ghi trong hồ sơ quyết định người đó thấy màn nào, được xếp vào ca nào, và lên bảng lương ở đâu.",
      the: ["staff"],
    },
    {
      ten: "Đổi bộ phận hoặc cơ sở",
      tone: "warning",
      ke: "Sửa tới đâu là đổi luôn tới đó: lịch ca, bảng lương và những gì họ nhìn thấy đều chạy theo hồ sơ mới.",
      the: ["staff"],
    },
    {
      ten: "Nghỉ việc",
      tone: "danger",
      nhanh: true,
      ke: "Chuyển hồ sơ sang nghỉ chứ đừng xoá — xoá là mất luôn số liệu các kỳ trước của người đó.",
      the: ["staff"],
    },
  ],
};

export const STAFF_GUIDE: MultiTabGuideConfig = {
  tabOrder: STAFF_GUIDE_TAB_ORDER,
  theAiMoDuoc: THE_AI_MO_DUOC,
  tone: TONE_THEO_NHAN,
  flow: STAFF_FLOW,
};
